import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, AlertCircle, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDashboardFiltersStore } from '@/store/dashboardFiltersStore';

export interface SdkVersionRow {
  platform: string;
  version: string;
  count: number;
}

interface SdkVersionChartProps {
  data?: SdkVersionRow[];
  loading?: boolean;
  error?: Error;
  height?: number;
  /**
   * Enable the cross-filter behaviour: clicking a row toggles a
   * dashboard-wide filter for the (platform, version) pair. Disabled by
   * default so the FaceRecognitionMetricsPage (which doesn't share the
   * digital-onboarding filter store) renders read-only rows.
   */
  enableCrossFilter?: boolean;
}

const PLATFORM_BADGE_VARIANT: Record<string, { label: string; className: string }> = {
  ANDROID: { label: 'Android', className: 'bg-green-500/10 text-green-700 border-green-500/30' },
  IOS: { label: 'iOS', className: 'bg-blue-500/10 text-blue-700 border-blue-500/30' },
  WEB: { label: 'Web', className: 'bg-purple-500/10 text-purple-700 border-purple-500/30' },
  FLUTTER: { label: 'Flutter', className: 'bg-sky-500/10 text-sky-700 border-sky-500/30' },
  UNKNOWN: { label: 'Unknown', className: 'bg-muted text-muted-foreground border-muted' },
};

function platformBadge(rawPlatform: string) {
  const upper = (rawPlatform || 'UNKNOWN').toUpperCase();
  return PLATFORM_BADGE_VARIANT[upper] ?? {
    label: rawPlatform || 'Unknown',
    className: 'bg-muted text-muted-foreground border-muted',
  };
}

export function SdkVersionChart({
  data,
  loading = false,
  error,
  height = 320,
  enableCrossFilter = false,
}: SdkVersionChartProps) {
  const { t } = useTranslation();
  const activeSdkVersion = useDashboardFiltersStore((s) => s.filters.sdkVersion);
  const toggleFilter = useDashboardFiltersStore((s) => s.toggleFilter);

  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.sdkVersions.title')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.sdkVersions.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-12" />
              </div>
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.sdkVersions.title')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.sdkVersions.errorDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center" style={{ minHeight: height }}>
          <div className="text-center space-y-3">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div>
              <h3 className="font-medium">{t('dashboard.sdkVersions.unableToDisplay')}</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.sdkVersions.title')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.sdkVersions.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center" style={{ minHeight: height }}>
          <div className="text-center text-muted-foreground">
            <RefreshCw className="h-6 w-6 mx-auto mb-2" />
            <p>{t('dashboard.sdkVersions.noData')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sortedData = [...data].sort((a, b) => b.count - a.count);
  const total = sortedData.reduce((sum, row) => sum + row.count, 0);
  const maxCount = sortedData[0].count;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">{t('dashboard.sdkVersions.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('dashboard.sdkVersions.totalEvents', {
            count: total,
            uniqueCount: sortedData.length,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-3 overflow-y-auto" style={{ maxHeight: height }}>
          {sortedData.map((row, index) => {
            const percentage = total > 0 ? (row.count / total) * 100 : 0;
            const barWidth = maxCount > 0 ? (row.count / maxCount) * 100 : 0;
            const badge = platformBadge(row.platform);
            // Composite key must match what the server stores: uppercase
            // platform + raw version, joined by "|". Same shape the API
            // emits in `sdkVersionCounter`.
            const filterValue = `${row.platform.toUpperCase()}|${row.version}`;
            const isActive = enableCrossFilter && activeSdkVersion === filterValue;
            const rowKey = `${row.platform}|${row.version}|${index}`;
            const body = (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Badge variant="outline" className={`text-xs ${badge.className}`}>
                      {badge.label}
                    </Badge>
                    <span className="font-mono text-sm truncate">{row.version}</span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="font-semibold text-sm">{row.count.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </>
            );
            if (!enableCrossFilter) {
              return (
                <div key={rowKey} className="space-y-1.5">
                  {body}
                </div>
              );
            }
            return (
              <button
                type="button"
                key={rowKey}
                onClick={() => toggleFilter('sdkVersion', filterValue)}
                aria-pressed={isActive}
                className={`w-full text-left space-y-1.5 p-2 -mx-2 rounded-md border border-transparent transition-colors hover:bg-accent/40 ${
                  isActive ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : ''
                }`}
              >
                {body}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
